import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { analytics } from '../utils/analytics';
import { usePermissions } from './usePermissions';
// import { RESOURCE_GRID_COPY } from '../utils/resourceGridCopy'; // Not used yet

export interface ResourceGridContext {
  scopeType: 'plan' | 'market' | 'provider' | 'practice';
  scopeId: string;
  activeFilters: Record<string, unknown>;
  columnsMode: 'plans' | 'providers';
}

export interface ResourceGridActions {
  onEditProviderCredentialStatus: (params: {
    providerId: string;
    planId?: string;
    marketId?: string;
    rowMeta?: unknown;
  }) => void;
  onBulkEditInsuranceRows: (context: ResourceGridContext) => void;
  onViewPlans: (providerId: string, context: ResourceGridContext) => void;
  canEditProviderCredentials: boolean;
  canBulkEditInsurance: boolean;
  canViewProviderPlans: boolean;
  showViewPlans: boolean;
  showBulkEdit: boolean;
}

export const useResourceGridActions = (
  context: ResourceGridContext
): ResourceGridActions => {
  const navigate = useNavigate();
  const {
    canEditInsurance,
    isAdmin,
    // Add more specific permissions as needed
  } = usePermissions();

  // Permission checks
  const canEditProviderCredentials = canEditInsurance() || isAdmin();
  const canBulkEditInsurance = canEditInsurance() || isAdmin();
  const canViewProviderPlans = true; // Add specific permission check when available

  // Show "View plans" option based on scope type and context
  // Hide view plans and bulk edit for all plans view and market detail pages
  const showViewPlans = context.scopeType !== 'plan' && context.scopeType !== 'market';
  const showBulkEdit = context.scopeType !== 'market';

  // Single-row edit handler
  const onEditProviderCredentialStatus = useCallback(
    ({ providerId, planId, marketId }: {
      providerId: string;
      planId?: string;
      marketId?: string;
      rowMeta?: unknown;
    }) => {
      // Track analytics
      analytics.track('rg.action_invoked', {
        action: 'single_edit',
        scopeType: context.scopeType,
        scopeId: context.scopeId,
        providerId,
        planId,
      });

      // Build query parameters for deep-linking. openInsuranceModal is what
      // ProviderDetail actually checks to auto-open the editor - without it
      // this just lands on the provider page with nothing open.
      const queryParams = new URLSearchParams({
        from: 'resource-grid',
        openInsuranceModal: 'true',
      });

      if (planId) queryParams.set('planId', planId);
      if (marketId) queryParams.set('marketId', marketId);

      // Navigate to provider detail with auto-open modal
      navigate(`/providers/${providerId}?${queryParams.toString()}`);
    },
    [navigate, context.scopeType, context.scopeId]
  );

  // Bulk edit handler
  const onBulkEditInsuranceRows = useCallback(
    (bulkContext: ResourceGridContext) => {
      // Track analytics
      analytics.track('rg.action_invoked', {
        action: 'bulk_edit',
        scopeType: context.scopeType,
        scopeId: context.scopeId,
      });

      // Navigate to bulk edit page with current context
      const queryParams = new URLSearchParams({
        from: 'resource-grid',
        scopeType: bulkContext.scopeType,
        scopeId: bulkContext.scopeId,
      });

      // Add active filters to query params
      Object.entries(bulkContext.activeFilters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.set(`filter_${key}`, String(value));
        }
      });

      navigate(`/plans/bulk-edit?${queryParams.toString()}`);
    },
    [navigate, context.scopeType, context.scopeId]
  );

  // View plans handler
  const onViewPlans = useCallback(
    (providerId: string, viewContext: ResourceGridContext) => {
      // Track analytics
      analytics.track('rg.action_invoked', {
        action: 'view_plans',
        scopeType: context.scopeType,
        scopeId: context.scopeId,
        providerId,
      });

      // Navigate to provider plans with UTM tracking
      const queryParams = new URLSearchParams({
        from: 'resource-grid',
        scopeType: viewContext.scopeType,
        scopeId: viewContext.scopeId,
      });

      // Navigate to plans list filtered by this provider
      queryParams.set('providerIds', providerId);
      navigate(`/plans?${queryParams.toString()}`);
    },
    [navigate, context.scopeType, context.scopeId]
  );

  return {
    onEditProviderCredentialStatus,
    onBulkEditInsuranceRows,
    onViewPlans,
    canEditProviderCredentials,
    canBulkEditInsurance,
    canViewProviderPlans,
    showViewPlans,
    showBulkEdit,
  };
};
